import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LayoutConfigService, SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {LayoutUtilsService} from '../../../../core/_base/crud';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    AssetClassType,
    AssetClassTypeUpdated,
    AssetClassTypeOnServerCreated,
    selectAssetClassTypesActionLoading,
    selectAssetClassTypeById,
    selectLastCreatedAssetClassTypeId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import { TopPropertyTypeModel } from 'src/app/core/linked-tables/_models/top-property-type.model';
import { AllTopPropertyTypesRequested } from 'src/app/core/linked-tables/_actions/top-property-type.actions';
import { selectAllTopPropertyTypes } from 'src/app/core/linked-tables/_selectors/top-property-type.selectors';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-asset-class-type-edit',
    templateUrl: './asset-class-type-edit.component.html',
    styleUrls: ['./asset-class-type-edit.component.scss'],
})


export class AssetClassTypeEditComponent implements OnInit, OnDestroy {

    model: AssetClassType;
    oldModel: AssetClassType;

    assetClassTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false;
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    propertyTypes: TopPropertyTypeModel[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private layoutUtilsService: LayoutUtilsService,
                private store: Store<AppState>,
                private layoutConfigService: LayoutConfigService,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectAssetClassTypesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectAssetClassTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initAssetClassType();
                    }
                });
            } else {
                this.model = new AssetClassType();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initAssetClassType();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }

        this.store.dispatch(new AllTopPropertyTypesRequested())
        this.store.select(selectAllTopPropertyTypes).subscribe(res => {
            this.propertyTypes = [];
            if (res) {
                this.propertyTypes = res;
            }
        })
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Approaches To Values', page: `../default/admin-management/linked-tables/asset-class-types`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/asset-class-types/edit/${id}`
                    : `../default/admin-management/linked-tables/asset-class-types/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initAssetClassType() {
        this.createForm();
    }


    createForm() {
        this.assetClassTypeForm = this.fb.group({
            name: [this.model.name, Validators.required],
            property_type_id: [this.model.top_property_type_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('ASSET_CLASS_TYPE.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('ASSET_CLASS_TYPE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.assetClassTypeForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.assetClassTypeForm.markAsPristine();
        this.assetClassTypeForm.markAsUntouched();
        this.assetClassTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.assetClassTypeForm.controls;
        /** check form */
        if (this.assetClassTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }
        this.model.top_property_type_id = this.assetClassTypeForm.controls.property_type_id.value;

        if (this.model.id > 0) {
            this.updateAssetClassType(this.model);
        } else {
            this.createAssetClassType(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: AssetClassTypeModel
     */
    updateAssetClassType(_model: AssetClassType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateAssetClassType: Update<AssetClassType> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new AssetClassTypeUpdated({
            partialassetClassType: updateAssetClassType,
            assetClassType: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: AssetClassTypeModel
     */
    createAssetClassType(_model: AssetClassType) {
        this.store.dispatch(new AssetClassTypeOnServerCreated({assetClassType: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedAssetClassTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model
            && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }



    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
